// The Research Packs this SEARCH SESSION has already been filed into (busy-my-web#364).
//
// Saffet, 2026-09-03: "when you save to a research pack it should be in the search Briefing".
//
// Saving results into a pack briefs that pack on the spot (`briefing/briefSavedPack`, #214) — but
// only until the next question is asked, when `packStopsLeading` hands the strip back to the search
// and the pack the results went into is gone from the box entirely. Everything else the session
// produced is still on screen; the one thing that was actually KEPT is not, and there is then no
// way back into it short of finding the Ongoing.
//
// So the search briefing carries the session's destinations with it. The record already exists and
// is durable: `MARK_TURNS_FILED` stamps every unfiled turn with the pack it went into
// (`filedPackId` / `filedPackTitle`, #157), which is the same stamp that retires the "Search opened"
// row from the story rail. Reading it here rather than keeping a second list is what stops the rail
// and the strip disagreeing about where a search ended up.
//
// Pure so it is unit-testable without a store or a mounted box.

/**
 * One entry per pack, in the order the session first filed into them.
 *
 * Deduped by pack id: a session that saves twice into the same pack has ONE destination, not two
 * lines saying the same name — and the turns keep their original stamp, so the earliest save is the
 * one that decides the order.
 *
 * @param turns the research session's turns (`research/turns`)
 * @param ongoingId the story those packs live on (`research/targetOngoingId`), when it is known —
 *        a pack is only ever viewable on its ongoing, and the strip may be briefing from home where
 *        nothing in the URL names it. Left off the entry entirely when there is none, so the
 *        Briefing's own resolution (`briefing/story`) still stands where it always did.
 * @returns {Array<{id: string, title: string, ongoingId?: string}>}
 */
export function savedPacksOfTurns(turns, ongoingId = null) {
  const seen = new Set()
  const out = []
  for (const turn of turns || []) {
    const id = String(turn?.filedPackId || '')
    if (!id || seen.has(id)) continue
    // NO FALLBACK TITLE (2026-09-15 parity audit E-8, busy-my-web#489). This used to read
    // `|| 'Research pack'`, with the note "a stamp made before the pack had a name still has to
    // READ as something". The audit walked the case it was written for and found the opposite:
    // the generic row told the user nothing, named no destination they could recognise, and —
    // because the fallback made every stamp look complete — hid the real fault, which is a save
    // that never learned the pack's title. `filedPackTitle` is now derived from what the save
    // endpoint actually returned (`saved[].title`, busy-main-bff merged notes 2026-09-15), so a
    // missing title means the save did not name a pack and there is no destination to list.
    const title = String(turn.filedPackTitle || '').trim()
    if (!title) continue
    seen.add(id)
    out.push({
      id,
      title,
      ...(ongoingId ? { ongoingId: String(ongoingId) } : {}),
    })
  }
  return out
}

/**
 * The destinations to show on the briefing for the turn the strip is CURRENTLY describing
 * (2026-09-15 parity audit E-8, busy-my-web#489).
 *
 * #364 listed the whole session's destinations under every turn, so that a pack "does not leave the
 * box the moment the next question is asked". The audit walked it (report E, finding #43) and found
 * the cost: the third search's briefing carried "Saved to / Research pack View ↗" before anything
 * had been ticked on it — a receipt for a save that had nothing to do with the results on screen —
 * and the Figma frame for a fresh search (40007675:331948) has no such row at all.
 *
 * So the rule is now the turn's own: a filed turn names every pack the session has reached (a
 * multi-pack save has several, and they belong to the turn that made it), an UNFILED turn names
 * none. #364's purpose survives where it was aimed — the strip still carries the destination for
 * as long as the saved results are the ones on screen — and the moment a new question replaces
 * them the row goes with them.
 *
 * @param turns the session's turns (`research/turns`)
 * @param ongoingId see `savedPacksOfTurns`
 */
export function savedPacksForCurrentTurn(turns, ongoingId = null) {
  const list = turns || []
  const last = list[list.length - 1]
  if (!last || !last.filedPackId) return []
  return savedPacksOfTurns(list, ongoingId)
}
